/**
 * Local mock of the §6.1 admin contract, so the FE can be built and verified
 * without the backend (BE-2…4). Enabled via `REACT_APP_USE_MOCK=true`
 * (see `adminService.ts`). Values mirror the design-reference prototype
 * (`docs/design-reference/README.md`): totalUsers 4.812, new-users 38/263/1.142,
 * active 2.946, subs trial 184 / active 1.732, expired the remainder.
 *
 * This is throwaway scaffolding — delete it once every query is wired to the
 * real backend. Do NOT let mock shapes drift from `types.ts`.
 */
import type {
  AdminDashboardKpis,
  AdminDashboardSeries,
  AdminImportOrigins,
  AdminFeatureUsage,
  AdminFeatureUsageSeries,
  AdminUsageFeature,
  AdminUsageSubStatus,
  AdminRangeVars,
  AdminSeriesPoint,
  AdminUserConnection,
  AdminUserRow,
  AdminUsersVars,
  UserSubStatus,
} from "../graphql/types";

// --- Deterministic PRNG (mulberry32) so mock data is stable across reloads ---
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const TOTAL_USERS = 4812;

// ---------------------------------------------------------------------------
// Dashboard KPIs
// ---------------------------------------------------------------------------

const KPI_BY_RANGE: Record<string, { newUsers: number; trendPct: number }> = {
  DAY: { newUsers: 38, trendPct: 12 },
  WEEK: { newUsers: 263, trendPct: 8 },
  MONTH: { newUsers: 1142, trendPct: 15 },
};

export function mockDashboardKpis(vars: AdminRangeVars): AdminDashboardKpis {
  let newUsers: number;
  let trendPct: number;

  if (vars.range === "CUSTOM" && vars.from && vars.to) {
    // Prototype mocks the custom KPI from the span length (days * 41).
    const days = Math.max(
      1,
      Math.round(
        (new Date(vars.to).getTime() - new Date(vars.from).getTime()) / 86_400_000,
      ),
    );
    newUsers = days * 41;
    trendPct = 10;
  } else {
    const preset = KPI_BY_RANGE[vars.range] ?? KPI_BY_RANGE.WEEK;
    newUsers = preset.newUsers;
    trendPct = preset.trendPct;
  }

  return {
    totalUsers: TOTAL_USERS,
    newUsers,
    newUsersTrendPct: trendPct,
    activeUsers30d: 2946,
    subscriptions: {
      trial: 184,
      active: 1732,
      expired: 421,
      free: TOTAL_USERS - 184 - 1732 - 421,
    },
  };
}

// ---------------------------------------------------------------------------
// Dashboard series (registrations / logins / source)
// ---------------------------------------------------------------------------

function buildSeries(
  labels: string[],
  seed: number,
  base: number,
  spread: number,
): AdminSeriesPoint[] {
  const rand = mulberry32(seed);
  return labels.map((label) => ({
    label,
    value: Math.round(base + rand() * spread),
  }));
}

function labelsForRange(vars: AdminRangeVars): string[] {
  switch (vars.range) {
    case "DAY":
      // 24 hourly points
      return Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);
    case "MONTH":
    case "CUSTOM":
      // ~16 points (prototype falls back to the month series for custom)
      return Array.from({ length: 16 }, (_, i) => `T${i + 1}`);
    case "WEEK":
    default:
      return ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  }
}

export function mockDashboardSeries(vars: AdminRangeVars): AdminDashboardSeries {
  const labels = labelsForRange(vars);
  const scale = vars.range === "DAY" ? 1 : vars.range === "WEEK" ? 6 : 18;
  const registrations = buildSeries(labels, 1337 + labels.length, 4 * scale, 8 * scale);
  const logins = buildSeries(labels, 4242 + labels.length, 20 * scale, 30 * scale);
  return {
    registrations,
    logins,
    registrationSource: { organic: registrations, referralPlanned: true },
  };
}

// ---------------------------------------------------------------------------
// Users list — a generated pool, filtered/sorted/paged server-side style
// ---------------------------------------------------------------------------

const FIRST_NAMES = [
  "Mara", "Tobias", "Yuki", "Daniel", "Sofia", "Liam", "Amara", "Jonas",
  "Priya", "Erik", "Nadia", "Marco", "Lena", "Noah", "Aisha", "Felix",
  "Clara", "Omar", "Ida", "Ben", "Sara", "Leon", "Mia", "Paul",
];
const LAST_NAMES = [
  "Lindqvist", "Kern", "Tanaka", "Osei", "Marchetti", "O'Connor", "Okafor",
  "Brandt", "Nair", "Sandberg", "Hassan", "Silva", "Weber", "Schmidt",
  "Rossi", "Novak", "Andersson", "Fischer", "Costa", "Meier",
];

// ---------------------------------------------------------------------------
// Import origins
// ---------------------------------------------------------------------------

const ORIGIN_BY_RANGE: Record<string, AdminImportOrigins> = {
  DAY: {
    songs: { chordProEditor: 74, openWith: 12, onSongImport: 148, songBookPlusImport: 61 },
    imports: { onSongBackup: 2, songBookPlus: 1 },
  },
  WEEK: {
    songs: { chordProEditor: 512, openWith: 88, onSongImport: 1043, songBookPlusImport: 396 },
    imports: { onSongBackup: 14, songBookPlus: 6 },
  },
  MONTH: {
    songs: { chordProEditor: 2184, openWith: 371, onSongImport: 4260, songBookPlusImport: 1608 },
    imports: { onSongBackup: 57, songBookPlus: 23 },
  },
};

export function mockImportOrigins(vars: AdminRangeVars): AdminImportOrigins {
  if (vars.range === "CUSTOM" && vars.from && vars.to) {
    // Scale the weekly shape by the span length, like the KPI mock does.
    const days = Math.max(
      1,
      Math.round(
        (new Date(vars.to).getTime() - new Date(vars.from).getTime()) / 86_400_000,
      ),
    );
    const factor = days / 7;
    const base = ORIGIN_BY_RANGE.WEEK;
    return {
      songs: {
        chordProEditor: Math.round(base.songs.chordProEditor * factor),
        openWith: Math.round(base.songs.openWith * factor),
        onSongImport: Math.round(base.songs.onSongImport * factor),
        songBookPlusImport: Math.round(base.songs.songBookPlusImport * factor),
      },
      imports: {
        onSongBackup: Math.round(base.imports.onSongBackup * factor),
        songBookPlus: Math.round(base.imports.songBookPlus * factor),
      },
    };
  }

  return ORIGIN_BY_RANGE[vars.range] ?? ORIGIN_BY_RANGE.WEEK;
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

function buildUserPool(): AdminUserRow[] {
  const rand = mulberry32(90210);
  const pool: AdminUserRow[] = [];
  const size = 120;
  const now = Date.now();

  for (let i = 0; i < size; i++) {
    const first = FIRST_NAMES[Math.floor(rand() * FIRST_NAMES.length)];
    const last = LAST_NAMES[Math.floor(rand() * LAST_NAMES.length)];
    const name = `${first} ${last}`;
    const email = `${first}.${last}`.toLowerCase().replace(/[^a-z.]/g, "") + `@example.com`;

    const roll = rand();
    const status: UserSubStatus = roll < 0.55 ? "ACTIVE" : roll < 0.82 ? "TRIAL" : "EXPIRED";
    const inTrial = status === "TRIAL";
    const trialDaysLeft = inTrial ? 1 + Math.floor(rand() * 24) : null;

    // lastLogin within the last ~60 days; a few never logged in.
    const lastLogin =
      rand() < 0.04
        ? null
        : new Date(now - Math.floor(rand() * 60 * 86_400_000)).toISOString();

    const projectsOwned = Math.floor(rand() * 4);
    const projectsInto = Math.floor(rand() * 5);

    // Most users type their songs by hand; imports cluster on a few users.
    const chordProEditor = Math.floor(rand() * 40);
    const openWith = Math.floor(rand() * 12);
    const onSongImport = rand() < 0.3 ? Math.floor(rand() * 120) : 0;
    const songBookPlusImport = rand() < 0.15 ? Math.floor(rand() * 80) : 0;

    pool.push({
      id: `u_${i + 1}`,
      name,
      email,
      lastLogin,
      subscriptionStatus: status,
      inTrial,
      trialDaysLeft,
      projectsOwned,
      projectsInto,
      songs: Math.floor(rand() * 240),
      setlists: Math.floor(rand() * 30),
      gigs: Math.floor(rand() * 18),
      songSources: { chordProEditor, openWith, onSongImport, songBookPlusImport },
    });
  }
  return pool;
}

const USER_POOL = buildUserPool();

export function mockUsers(vars: AdminUsersVars): AdminUserConnection {
  const {
    search = "",
    status = "ALL",
    sort = "LOGIN",
    sortDir = "DESC",
    skip = 0,
    take = 50,
  } = vars;

  const q = (search ?? "").trim().toLowerCase();
  let filtered = USER_POOL.filter((u) => {
    if (q && !`${u.name} ${u.email}`.toLowerCase().includes(q)) return false;
    if (status !== "ALL" && u.subscriptionStatus !== status) return false;
    return true;
  });

  const dir = sortDir === "ASC" ? 1 : -1;
  filtered = [...filtered].sort((a, b) => {
    switch (sort) {
      case "NAME":
        return dir * a.name.localeCompare(b.name);
      case "PROJECTS":
        return dir * (a.projectsOwned + a.projectsInto - (b.projectsOwned + b.projectsInto));
      case "LOGIN":
      default: {
        const at = a.lastLogin ? Date.parse(a.lastLogin) : 0;
        const bt = b.lastLogin ? Date.parse(b.lastLogin) : 0;
        return dir * (at - bt);
      }
    }
  });

  return {
    totalCount: TOTAL_USERS,
    filteredCount: filtered.length,
    items: filtered.slice(skip, skip + take),
  };
}

// ---------------------------------------------------------------------------
// Feature usage (§6.6)
// ---------------------------------------------------------------------------

const MINIMUM_CELL_SESSIONS = 5;

/**
 * Hand-shaped so every state the view can show actually occurs:
 * healthy curves, a few withheld points, and one series that is withheld
 * entirely. Trial is thinner than Active, which is what the beta looks like.
 */
const USAGE_SHAPE: Record<
  AdminUsageFeature,
  { active: number; trial: number; minutesPerSession: number } | null
> = {
  SONG_SCREEN: { active: 12, trial: 7, minutesPerSession: 4 },
  SETLIST_SCREEN: { active: 8, trial: 6, minutesPerSession: 3 },
  // Trial stays under the bar on every bucket → the whole series is withheld.
  GIG_SCREEN: { active: 6, trial: 2, minutesPerSession: 2 },
  HOME_SCREEN: { active: 14, trial: 9, minutesPerSession: 1 },
  EDITOR: { active: 9, trial: 5, minutesPerSession: 7 },
  LIVE_MODE: { active: 11, trial: 6, minutesPerSession: 26 },
  // Nothing in the app emits this yet.
  DIRECTED_LIVE: null,
};

export function mockFeatureUsage(vars: AdminRangeVars): AdminFeatureUsage {
  // Usage carries no time of day, so the backend collapses a day range to days.
  const collapsed = vars.range === "DAY";
  const labels = collapsed ? ["Heute"] : labelsForRange(vars);

  const series: AdminFeatureUsageSeries[] = [];
  let aggregatedSeconds = 0;
  let aggregatedSessions = 0;

  const features = Object.keys(USAGE_SHAPE) as AdminUsageFeature[];

  features.forEach((feature, featureIndex) => {
    const shape = USAGE_SHAPE[feature];
    if (!shape) return;

    (["TRIAL", "ACTIVE"] as AdminUsageSubStatus[]).forEach((subStatus, statusIndex) => {
      const rand = mulberry32(7000 + featureIndex * 13 + statusIndex);
      const base = subStatus === "ACTIVE" ? shape.active : shape.trial;

      const raw = labels.map((label, bucketIndex) => {
        // One deliberate dip per series so a withheld point is always visible.
        const dip = bucketIndex === 2 && base >= MINIMUM_CELL_SESSIONS;
        const sessions = dip
          ? MINIMUM_CELL_SESSIONS - 2
          : Math.max(0, base - 2 + Math.round(rand() * 4));
        const seconds = Math.round(
          sessions * shape.minutesPerSession * 60 * (0.7 + rand() * 0.6),
        );
        return { label, sessions, seconds };
      });

      const totalSeconds = raw.reduce((sum, point) => sum + point.seconds, 0);
      const totalSessions = raw.reduce((sum, point) => sum + point.sessions, 0);

      if (totalSessions < MINIMUM_CELL_SESSIONS) {
        aggregatedSeconds += totalSeconds;
        aggregatedSessions += totalSessions;
        return;
      }

      const points = raw.map((point) => {
        if (point.sessions < MINIMUM_CELL_SESSIONS) {
          aggregatedSeconds += point.seconds;
          aggregatedSessions += point.sessions;
          return { label: point.label, seconds: 0, sessions: 0, aggregated: true };
        }
        return { ...point, aggregated: false };
      });

      series.push({ feature, subStatus, points, totalSeconds, totalSessions });
    });
  });

  return {
    series,
    aggregated: { seconds: aggregatedSeconds, sessions: aggregatedSessions },
    optOut: { users: 4812, optedOut: 391, rate: 391 / 4812 },
    minimumCellSessions: MINIMUM_CELL_SESSIONS,
    bucketsCollapsedToDays: collapsed,
  };
}
